import { type Shift } from "@/lib/backend/api";
import { isSettled, toEntryState } from "@/lib/shift/entry-status";

/**
 * The subset of a shift needed to decide whether the employee still owes hours. Kept structural so
 * both the list queries and the overview hook can pass their own shapes.
 */
export type HoursSubmissionCandidate = Pick<Shift, "shiftTimeEntry" | "project">;

/**
 * Whether this shift is still waiting for the employee to submit their hours.
 *
 * Three screens used to answer this question with three slightly different expressions — the Shifts
 * tab counted only a missing `shiftTimeEntry.id`, while the completed page also treated a
 * half-filled entry (no start, no end, or no break) as outstanding. That made the badge on one
 * screen disagree with the list on the next. This is the single answer: an entry counts as
 * submitted only when it is complete.
 */
export function needsHoursSubmission(shift: HoursSubmissionCandidate): boolean {
  if (!shift.project?.allowEmployeeSubmitHours) {
    return false;
  }

  // A finalized project is closed for changes — nagging about it would be a dead end.
  if (shift.project?.status === "FINALIZED") {
    return false;
  }

  const entry = shift.shiftTimeEntry;

  // Once the office has signed off, the question is closed regardless of what the fields look
  // like. Approved and paid entries were landing in the action list because the completeness
  // check ran first and some of them carry no break time — so the app asked people to submit
  // hours that were already on their payslip, and offered a button to do it with.
  if (isSettled(toEntryState(entry?.status))) {
    return false;
  }

  return (
    !entry?.id ||
    !entry?.startAt ||
    !entry?.endAt ||
    entry?.breakTimeMinutes === null ||
    entry?.breakTimeMinutes === undefined
  );
}
